/**
 * api-fetch/templates — verbatim KanbanApi method block extracted from the
 * former api.fetch.ts monolith. Closes over the shared {@link ApiFetchContext};
 * behaviour is unchanged. Composed by spread in createKanbanFetchApi.
 */
import type {
  ActionExecution,
  ActionTemplate,
  Allocation,
  Board,
  BoardTemplate,
  BoardTemplateAuditLogPage,
  BoardTemplateIntegrityReport,
  Card,
  CardAiThread,
  CardAttachment,
  CardChecklistItemT,
  CardChecklistT,
  CardCommentT,
  CardDependency,
  CostIntelligenceSummary,
  CreateBoardTemplateInput,
  DuplicateBoardTemplateInput,
  UpdateBoardTemplateInput,
  QuoteStageSummaryRow,
  QuoteConvertOptions,
  QuoteConvertResult,
  TrelloCardDetail,
  CardScheduleContext,
  ClassificationRule,
  EnsureTemplateLaneIdsResult,
  Lane,
  BoardMembership,
  LaneMembership,
  LinkBoardToTemplateInput,
  LinkBoardToTemplateResult,
  ListAuditLogOptions,
  ListBoardTemplatesOptions,
  ListWorkflowPresetsOptions,
  BoardRelatedCounts,
  BoardUnseenEmails,
  Notebook,
  NotebookActionItemT,
  NotebookAddCardsResult,
  NotebookListItem,
  NotebookRecordingT,
  OrgClassificationMemory,
  ReconcileBoardInput,
  ReconcileResult,
  RelatedCards,
  Tag,
  UnlinkBoardFromTemplateInput,
  WorkflowPreset,
} from '../types'

import type {
  ActionInvokeInput,
  ActionTemplateFilter,
  AllocateEmailToLaneInput,
  AllocateEmailToLaneResult,
  AllocationExplain,
  AllocationCreate,
  AllocationFilter,
  BoardCreate,
  BoardDefaultCategory,
  BoardListFilter,
  BoardSnapshot,
  BoardSummary,
  CardCreate,
  CardParseActionsResult,
  CardDependencyCreate,
  CardDependencyFilter,
  CardFilter,
  CardSearchResult,
  DueSoonFilter,
  LinkEmailToCardInput,
  LinkEmailToCardResult,
  KanbanCalendarActionExtract,
  AssignedToMeFilter,
  CardMoveInput,
  CardTeamMessage,
  CardTeamMessageListResponse,
  CardTeamsThread,
  KanbanAllocationConflict,
  KanbanApi,
  KanbanBoardVersion,
  KanbanCasOpts,
  KanbanSearchResponse,
  LaneCardCount,
  LaneCreate,
  LaneListOptions,
  InstallWorkflowPresetInput,
  InstallWorkflowPresetResult,
  PromoteLaneToBoardResult,
  SeedDefaultBoardsInput,
  SeedDefaultBoardsResult,
  TagCreate,
  TeamMemberAddInput,
  TeamMemberAddResult,
  TeamRosterSyncResult,
  TrelloCardWritebackPatch,
  TrelloCardWritebackResult,
  TrelloSyncStatusResponse,
  TrelloConnectionSettings,
  TrelloConnectionSettingsPatch,
  TrelloCardDriftList,
  TrelloCardDriftResolveResult,
  SyncDriftResolution,
} from '../api'
import { KanbanCardConflictError, KanbanNetworkError } from '../api'
import type { TrelloEmailLinkDecisionResult } from '../types'
import type {
  BoardMeeting,
  MeetingAnalyzeResult,
  MeetingLivePayload,
  MeetingSegmentIngestResult,
  MeetingSuggestionDecision,
} from '../meetingTypes'
import type { ApiFetchContext } from './context'

export function createTemplatesApi(ctx: ApiFetchContext) {
  const { _request, _trelloRequest, _casHeaders, getAuthHeaders, baseUrl, buildUrl } = ctx
  return {
    // Board templates + workflow presets (Stack 11 — C3) ---------------
    //
    // Thin pass-throughs to the BE endpoints documented in
    // ``email_kanban/views/templates.py`` (read-only) and
    // ``email_kanban/views/board_template_linkage.py`` (write).
    // No client-side caching here — callers wire React-Query around
    // these for cache + invalidation. Naming follows the FE
    // ``camelCase`` convention; URL paths follow the BE
    // ``kebab-case`` convention.

    listBoardTemplates: (opts?: ListBoardTemplatesOptions) =>
      _request<BoardTemplate[]>('GET', '/templates/', undefined, {
        kind: opts?.kind,
        is_system:
          opts?.isSystem === undefined ? undefined : opts.isSystem ? 'true' : 'false',
        include_archived: opts?.includeArchived ? 'true' : undefined,
      }),

    getBoardTemplate: (id: string) =>
      _request<BoardTemplate>('GET', `/templates/${id}/`),

    // Template-hub writes. Bodies are already snake_case because the
    // template payload IS the BE shape (lane JSON round-trips verbatim);
    // inventing a camelCase mirror here would mean re-keying every lane
    // entry on save, which is exactly how lane ids get dropped.
    createBoardTemplate: (input: CreateBoardTemplateInput) =>
      _request<BoardTemplate>('POST', '/templates/', input),

    updateBoardTemplate: (id: string, patch: UpdateBoardTemplateInput) =>
      _request<BoardTemplate>('PATCH', `/templates/${id}/`, patch),

    duplicateBoardTemplate: (id: string, input?: DuplicateBoardTemplateInput) =>
      _request<BoardTemplate>('POST', `/templates/${id}/duplicate/`, {
        name: input?.name,
      }),

    setBoardTemplateDefault: (id: string) =>
      _request<BoardTemplate>('POST', `/templates/${id}/set-default/`),

    clearBoardTemplateDefault: (id: string) =>
      _request<BoardTemplate>('POST', `/templates/${id}/clear-default/`),

    archiveBoardTemplate: (id: string) =>
      _request<BoardTemplate>('POST', `/templates/${id}/archive/`),

    restoreBoardTemplate: (id: string) =>
      _request<BoardTemplate>('POST', `/templates/${id}/restore/`),

    listWorkflowPresets: (opts?: ListWorkflowPresetsOptions) =>
      _request<WorkflowPreset[]>('GET', '/workflow-presets/', undefined, {
        vertical: opts?.vertical,
        is_system:
          opts?.isSystem === undefined ? undefined : opts.isSystem ? 'true' : 'false',
      }),

    getWorkflowPreset: (id: string) =>
      _request<WorkflowPreset>('GET', `/workflow-presets/${id}/`),

    installWorkflowPreset: (id: string, input?: InstallWorkflowPresetInput) =>
      _request<InstallWorkflowPresetResult>(
        'POST',
        `/workflow-presets/${id}/install/`,
        { organisation_id: input?.organisationId },
      ),

    linkBoardToTemplate: (templateId: string, input: LinkBoardToTemplateInput) =>
      _request<LinkBoardToTemplateResult>(
        'POST',
        `/board-template-linkage/${templateId}/link-board/`,
        {
          board_id: input.boardId,
          // Default ``true`` matches the BE default; the FE preview
          // flow sends ``false`` so it can show a diff before
          // cascading.
          reconcile: input.reconcile !== false,
        },
      ),

    unlinkBoardFromTemplate: async (
      templateId: string,
      input: UnlinkBoardFromTemplateInput,
    ) => {
      await _request<{ unlinked: boolean; board_id: string; template_id: string }>(
        'POST',
        `/board-template-linkage/${templateId}/unlink-board/`,
        { board_id: input.boardId },
      )
    },

    reconcileBoardToTemplate: async (
      templateId: string,
      input: ReconcileBoardInput,
    ) => {
      const wrapper = await _request<{ reconcile: ReconcileResult }>(
        'POST',
        `/board-template-linkage/${templateId}/reconcile-board/`,
        { board_id: input.boardId },
      )
      return wrapper.reconcile
    },

    reconcileAllLinkedBoards: async (templateId: string) => {
      const wrapper = await _request<{ results: ReconcileResult[] }>(
        'POST',
        `/board-template-linkage/${templateId}/reconcile-all-linked/`,
      )
      return wrapper.results
    },

    ensureTemplateLaneIds: (templateId: string) =>
      _request<EnsureTemplateLaneIdsResult>(
        'POST',
        `/board-template-linkage/${templateId}/ensure-lane-ids/`,
      ),

    getTemplateIntegrity: (templateId: string) =>
      _request<BoardTemplateIntegrityReport>(
        'GET',
        `/board-template-linkage/${templateId}/integrity/`,
      ),

    listTemplateAuditLog: (templateId: string, opts?: ListAuditLogOptions) =>
      _request<BoardTemplateAuditLogPage>(
        'GET',
        `/board-template-linkage/${templateId}/audit-log/`,
        undefined,
        {
          board_id: opts?.boardId,
          event_type: opts?.eventType,
          limit: opts?.limit,
          offset: opts?.offset,
        },
      ),

    // FEAT-2026-06-22-KANBAN-NOTEBOOKS — cards → AI-structured notebook.
    // camelCase FE convention ↔ snake_case BE body; URL paths kebab/snake
    // to match ``email_kanban/urls.py``.
    createNotebook: (input) =>
      _request<Notebook>('POST', '/notebooks/', {
        card_ids: input.cardIds,
        title: input.title,
        board: input.board ?? undefined,
        source_signal: input.sourceSignal,
        // Omit when undefined so the BE default (generate:true) applies.
        generate: input.generate,
      }),
    listNotebooks: async (filter) => {
      // Same pagination-resilience pattern as listBoards: DRF may return a
      // bare array (small set) or `{results}` (paginated). Unwrap both.
      const raw = await _request<unknown>('GET', '/notebooks/', undefined, {
        board: filter?.board,
        status: filter?.status,
        mine: filter?.mine ? 'true' : undefined,
      })
      if (Array.isArray(raw)) return raw as NotebookListItem[]
      const page = raw as { results?: NotebookListItem[] }
      return page?.results ?? []
    },
    getNotebook: (id) => _request<Notebook>('GET', `/notebooks/${id}/`),
    // U22 — append the selection to an EXISTING notebook (dupe-safe,
    // position-appended server-side).
    addNotebookCards: (notebookId, cardIds) =>
      _request<NotebookAddCardsResult>(
        'POST',
        `/notebooks/${notebookId}/cards/`,
        { card_ids: cardIds },
      ),
    patchNotebook: (id, patch) =>
      _request<Notebook>('PATCH', `/notebooks/${id}/`, patch),
    deleteNotebook: async (id) => {
      await _request<void>('DELETE', `/notebooks/${id}/`)
    },
    createNotebookActionItem: (input) =>
      _request<NotebookActionItemT>('POST', '/notebook-action-items/', input),
    updateNotebookActionItem: (itemId, patch) =>
      _request<NotebookActionItemT>(
        'PATCH', `/notebook-action-items/${itemId}/`, patch,
      ),
    deleteNotebookActionItem: async (itemId) => {
      await _request<void>('DELETE', `/notebook-action-items/${itemId}/`)
    },

    // FEAT-2026-06-22-KANBAN-NOTEBOOKS Phase 4 — recordings, PDF, related chip.
    createNotebookRecording: (input) =>
      _request<NotebookRecordingT>('POST', '/notebook-recordings/', {
        notebook: input.notebook,
        transcript: input.transcript,
        title: input.title,
        language: input.language,
        duration_ms: input.duration_ms ?? undefined,
      }),
    deleteNotebookRecording: async (recordingId) => {
      await _request<void>('DELETE', `/notebook-recordings/${recordingId}/`)
    },
    getNotebookPdf: async (id) => {
      // PDF is binary, so it bypasses `_request` (which assumes JSON) and
      // fetches the blob directly — same auth-header + credentials contract.
      const headers: Record<string, string> = { Accept: 'application/pdf' }
      if (getAuthHeaders) Object.assign(headers, await getAuthHeaders())
      const res = await fetch(buildUrl(baseUrl, `/notebooks/${id}/pdf/`), {
        method: 'GET',
        headers,
        credentials: 'include',
      })
      if (!res.ok) {
        const e = new Error(`http_${res.status}`) as Error & { status?: number }
        e.status = res.status
        throw e
      }
      return res.blob()
    },
    getBoardRelatedCounts: (boardId) =>
      _request<BoardRelatedCounts>(
        'GET', `/boards/${boardId}/related-counts/`,
      ),
    // ARC-5 U18 — per-user seen-state ("Focus: unseen").
    getBoardUnseenEmails: (boardId) =>
      _request<BoardUnseenEmails>(
        'GET', `/boards/${boardId}/unseen-emails/`,
      ),
    markEmailsSeen: (emailIds, source) =>
      _request<{ new: number }>('POST', '/emails/mark-seen/', {
        email_ids: emailIds,
        ...(source ? { source } : {}),
      }),
    // satisfies Partial<KanbanApi> — see api-fetch/cards.ts for the rationale
    // (back-propagates KanbanApi param types under tenant strict mode).
  } satisfies Partial<KanbanApi>
}
